/**
 * FAQ — คำถาม-คำตอบที่แอดมินตั้งให้บอตตอบเองโดยตรง (priority สูงกว่า KB/RAG)
 * บอตตรวจ keyword ในข้อความลูกค้า → ตรง FAQ ไหน → ตอบทันที
 * เชื่อมกับ: หน้า "ทดสอบบอต" (พิมพ์คำถาม → ดูคำตอบ + ป้าย "ตอบจาก FAQ")
 *
 * ใช้ CrudManager กลาง + suggester (เสนอ FAQ จากแชท) เป็น headerExtra
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CrudManager, useBrandOptions, type CrudField } from '@/components/CrudManager';
import { FaqApi, KbApi, type FaqItem, type KbFaqSuggestion } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

function FaqSuggester() {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const sQ = useQuery({ queryKey: ['faq-suggest'], queryFn: () => KbApi.suggestFaq('scrub'), enabled: show });
  const add = useMutation({
    mutationFn: (s: KbFaqSuggestion) => FaqApi.create({ brand: 'scrub', category: s.category, question: s.question, keywords: s.keywords, answer: s.answer, priority: 60 }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['faqs'] }); qc.invalidateQueries({ queryKey: ['faq-suggest'] }); toast.success('เพิ่มเป็น FAQ แล้ว — แก้ต่อได้ด้านล่าง'); },
    onError: (e: Error) => toast.error('ผิดพลาด: ' + e.message),
  });
  return (
    <div className="border rounded-lg p-3 bg-muted/20">
      <Button size="sm" variant="outline" onClick={() => setShow((v) => !v)}>✨ เสนอ FAQ จากแชทจริง {show ? '▲' : '▼'}</Button>
      {show && (
        sQ.isLoading ? <div className="text-xs text-muted-foreground mt-2">กำลังวิเคราะห์แชท…</div> : (
          <div className="space-y-2 mt-2">
            {(sQ.data ?? []).map((s, i) => (
              <div key={i} className="border rounded p-2">
                <div className="font-medium text-sm">{s.question}</div>
                <div className="text-xs text-muted-foreground whitespace-pre-wrap mb-1">{s.answer}</div>
                <div className="text-[10px] text-muted-foreground mb-1">🔑 {s.keywords}</div>
                <Button size="sm" className="h-6 text-[11px]" onClick={() => add.mutate(s)}>{s.alreadyInFaq ? 'เพิ่มอีกอัน' : 'เพิ่มเป็น FAQ'}</Button>
              </div>
            ))}
            {(sQ.data ?? []).length === 0 && <div className="text-xs text-muted-foreground">ยังไม่มีคำแนะนำ</div>}
          </div>
        )
      )}
    </div>
  );
}

export default function FaqPage() {
  const brandOpts = useBrandOptions(true);
  const FIELDS: CrudField[] = [
    { key: 'brand', label: 'แบรนด์', type: 'select', required: true, options: brandOpts, help: 'FAQ นี้ใช้กับแบรนด์ไหน (ทุกแบรนด์ = ใช้ร่วมทุกเพจ)' },
    { key: 'category', label: 'หมวด', type: 'text', required: true, default: 'ทั่วไป', placeholder: 'การชำระเงิน / สินค้า / ไซส์', help: 'จัดกลุ่ม FAQ ให้หาง่าย' },
    { key: 'question', label: 'คำถาม (สำหรับแอดมินอ่าน)', type: 'text', required: true, placeholder: 'เช่น มีปลายทางไหม', help: 'หัวข้อให้แอดมินจำง่าย (ลูกค้าไม่เห็น)' },
    { key: 'keywords', label: 'คำที่ลูกค้าพิมพ์ (trigger)', type: 'chips', required: true, placeholder: 'ปลายทาง, cod, เก็บปลายทาง', help: 'บอตจับคำเหล่านี้ในข้อความลูกค้า → ตอบ FAQ นี้ทันที' },
    { key: 'answer', label: 'คำตอบ (บอตส่งให้ลูกค้าตรงๆ)', type: 'textarea', required: true, placeholder: 'คำตอบที่บอตจะส่ง...', help: 'ข้อความที่ลูกค้าจะได้รับ' },
    { key: 'priority', label: 'ลำดับความสำคัญ', type: 'number', default: 50, help: 'เลขสูง = บอตเลือกตอบก่อน (ถ้าหลายอันแมตช์)' },
    { key: 'imageUrl', label: 'รูปแนบ (ถ้ามี)', type: 'image', help: 'ส่งรูปพร้อมคำตอบให้ลูกค้า' },
    { key: 'notes', label: 'หมายเหตุภายใน', type: 'text', help: 'ไม่ส่งให้ลูกค้า' },
    { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', default: true },
  ];
  return (
    <CrudManager<FaqItem>
      title="❓ คำถาม-คำตอบ (FAQ)"
      addLabel="เพิ่ม FAQ"
      help="บอตตรวจ keyword ในข้อความลูกค้า → ถ้าตรง FAQ ไหน → ตอบทันที (priority สูงกว่า KB/RAG). ใช้ comma คั่นคำ"
      queryKey="faqs"
      api={FaqApi}
      fields={FIELDS}
      brandScoped
      brandAll
      multiSelect
      searchKeys={['question', 'answer', 'keywords', 'category']}
      groupBy={(f) => f.category || 'ทั่วไป'}
      sortRows={(a, b) => (b.priority ?? 0) - (a.priority ?? 0) || a.id - b.id}
      rowTitle={(f) => <>{f.question}</>}
      rowSubtitle={(f) => (
        <>
          <span className="block text-foreground whitespace-pre-wrap">💬 {f.answer}</span>
          <span className="block text-[11px] text-muted-foreground mt-0.5">🔑 {f.keywords}</span>
        </>
      )}
      headerExtra={<FaqSuggester />}
    />
  );
}
